import { useEffect, useState } from 'react';
import { useDataProvider, useInput } from 'react-admin';
import { AutocompleteArrayInput } from 'react-admin';
import { useFormContext } from 'react-hook-form';

interface CmsAutocompleteArrayInputProps {
  source: string;
  label: string;
  language: string;
  sourceUrl: string;
  validate?: never;
}

interface Suggestion {
  // The ID is the option.
  // React-Admin needs each entity in a "list" response (e.g.,
  // /media/keywords) to have an ID property,
  // but since each option should be unique anyway,
  // there is no point in having a separate ID and separate option value.
  id: string;
  // Matches the ID - needed to prevent duplicate options from appearing
  // in the search list
  key: string;
}

// Limit how big a option can be
// for efficiency purposes
const SUGGESTION_INPUT_LENGTH_LIMIT = 3;

/**
 * Using this to get the full list of options
 * that the CMS user can browse to get the option they want.
 * The option list gets filtered as the user types more characters.
 */
const OPTION_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

/**
 * Extension of React Admin's AutocompleteArrayInput
 * Allows the user to query options from the API, of a certain format:
 * [{"id": "option1"}, {"id": "option2"}]
 * ALSO allows the user to create their own options
 */
export const CmsAutocompleteArrayInput = (props: CmsAutocompleteArrayInputProps) => {
  const { source, label, sourceUrl, validate } = props;
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const dataProvider = useDataProvider();

  const input = useInput({ source });

  const [inputValue, setInputValue] = useState('');

  const { clearErrors } = useFormContext();

  useEffect(() => {
    /**
      There are some cases where the initial input is a string,
      and some where the initial input is an array.
      I'm having trouble figuring out which cases are which,
      but I think it has something to do with navigating between different pages
      versus straight to the page this component is on via a URL.
      - Sunny Junejo, 13 March 2023
     */
    if (!input.field.value || input.field.value.length === 0) {
      return;
    }
    const selected = Array.isArray(input.field.value) ? input.field.value : input.field.value.split(',');
    const selectedRecords: Suggestion[] = selected
      // filter out empty string values if they exist (e.g. API returns en: [""])
      .filter((val: string) => val !== '' || val === null)
      .map((val: string) => ({ id: val, key: val }));
    // Merge selected values into suggestions instead of replacing the list.
    // Replacing wipes API-fetched dropdown options every time the field value
    // changes, which caused chips/dropdown options to flicker on selection.
    setSuggestions((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const additions = selectedRecords.filter((r) => !existingIds.has(r.id));
      return additions.length === 0 ? prev : [...prev, ...additions];
    });
    // Using `input.field` only leads to stack overflow, so not sure why
    // this is complaining!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.field.value]);

  const handleCreate = (newOption: string) => {
    // Remove whitespace
    newOption = newOption.trim();
    setSuggestions((prev) =>
      prev.some((s) => s.id === newOption) ? prev : [...prev, { id: newOption, key: newOption }]
    );
    return { id: newOption };
  };

  /**
   * I tried ReferenceInput but couldn't get it to work with options that a user has created but not saved,
   * so manually calling required endpoints instead
   * - Sunny Junejo, 13 Mar 2023
   */
  const fetchSuggestions = async (inputValue: string) => {
    if (inputValue.length < SUGGESTION_INPUT_LENGTH_LIMIT) {
      return;
    }
    const { data } = await dataProvider.getList(sourceUrl, {
      pagination: { page: 1, perPage: OPTION_PAGE_SIZE },
      sort: { field: 'id', order: 'ASC' },
      filter: { language: props.language, keyword: inputValue },
    });
    if (data.length === 0) {
      return;
    }
    setSuggestions((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const additions = data
        .filter((apiSuggestion) => !existingIds.has(apiSuggestion.id))
        .map((apiSuggestion) => ({ ...apiSuggestion, key: apiSuggestion.id }));
      return additions.length === 0 ? prev : [...prev, ...additions];
    });
  };

  const formatInputValue = (values: any) => {
    // Initial state (no options have ever been assigned)
    if (values === null) {
      return '';
    }
    // Caters for some edge cases I've seen
    if (!Array.isArray(values)) {
      return values.split(',');
    }
    return values;
  };

  return (
    <AutocompleteArrayInput
      variant="outlined"
      source={source}
      autoHighlight
      label={label}
      choices={suggestions}
      format={formatInputValue}
      helperText={false}
      optionText={(record) => {
        // 1st case handles where API returns e.g. "en": [],
        // (When a option was entered, then deleted),
        // 2nd case prevents showing "undefined" for 'null' entries,
        // e.g. when the media has never had options before.
        if ((Array.isArray(record) && record.length === 0) || !record.id) {
          return '';
        }
        return record.id;
      }}
      inputValue={inputValue}
      parse={(value) => {
        // Have to do some strange stuff here
        // to get Enter Key usage working
        const newValue = value.slice(-1)[0];
        if (!newValue) {
          const nv = [...value.slice(0, -1), inputValue];
          setInputValue('');
          return nv;
        }
        setInputValue('');
        return value;
      }}
      onChange={(_event) => {
        /**
          This hack is required to clear any error
          after selecting a new option
         */
        clearErrors();
      }}
      onCreate={(newOption) => handleCreate(newOption!)}
      setFilter={fetchSuggestions}
      validate={validate}
      blurOnSelect={false}
      TextFieldProps={{
        onKeyDown: (event) => {
          // Pressing a comma will add an option
          // (in addition to clicking manually, and the Enter key)
          if (event.key === ',') {
            event.preventDefault();
            if (inputValue.trim() !== '') {
              // Build a new array — never mutate input.field.value in place,
              // or RHF's change detection will miss the update and revert
              // the UI on the next render.
              const current = Array.isArray(input.field.value)
                ? input.field.value
                : (input.field.value ?? '').split(',').filter((v: string) => v !== '');
              input.field.onChange([...current, inputValue]);
              setInputValue('');
            }
          }
        },
        onChange: (event) => {
          setInputValue(event.target.value);
        },
      }}
    />
  );
};
